<script context="module" lang="ts">
  export const key = 'navbarModal';
  const closeAt = 768;
</script>

<script lang="ts">
  import { cx } from '@emotion/css';
  import { newsTagPagePath } from '$lib/utils/routes';
  import { ResponsiveVars, TwistDivider, TwistStack, type FluidProp } from '@twist/core';
  import type { NewsSecondaryNav } from '$lib/sanity/schemas';
  import AccessibilityControls from '$components/GlobalNav/Accessibility/AccessibilityControls.svelte';
  import Times from '@twist/icons/lib/Times.svg?component';
  import Modal from '$components/Modals/Modal.svelte';
  import BlockButton from '$components/Button/BlockButton.svelte';
  import {
    useEncodeDataAttribute,
    useQuery,
    type QueryResponseInitial,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import { sanityStudioURL } from '$lib/client';
  import { GetNewsSecondaryNav } from '$lib/sanity/queries/news';

  export let document: QueryResponseInitial<NewsSecondaryNav>;
  export let path: StudioPathLike = [];

  const query = useQuery<NewsSecondaryNav>(GetNewsSecondaryNav, undefined, {
    initial: document
  });

  $: ({ data: nav, sourceMap } = $query);
  $: encodeDataAttribute = useEncodeDataAttribute(nav, sourceMap, sanityStudioURL);

  const responsiveVars = new ResponsiveVars()
    .addFluid('close-size', [
      ['min', 54],
      ['sm', 'lg', 54, 108]
    ])
    .addFluid('close-spacer', [
      ['min', 24],
      ['sm', 'lg', 24, 48]
    ])
    .addFluid('close-icon-size', [
      ['min', 24],
      ['sm', 'lg', 24, 48]
    ])
    .toCSS();

  const topLevelButtonStyles: Record<string, FluidProp> = {
    height: [
      ['min', 48],
      ['sm', 'lg', 48, 96],
      ['lg', 96]
    ],
    fontSize: [
      ['min', 22],
      ['sm', 'lg', 22, 44],
      ['lg', 44]
    ],
    py: [
      ['min', 11],
      ['sm', 'lg', 11, 20],
      ['lg', 20]
    ],
    px: [
      ['min', 20],
      ['sm', 'lg', 20, 40],
      ['lg', 40]
    ]
  };
</script>

<Modal
  modalKey={key}
  autoCloseAt={closeAt}
  ariaLabel="Navigation Menu"
  size="fullscreen"
  let:onCloseClicked={handleCloseClicked}
>
  <div class={cx(responsiveVars, 'content')}>
    <button class="close" on:click={handleCloseClicked} aria-label="close navigation modal">
      <div class="icon">
        <Times />
      </div>
    </button>
    <div class="links-container">
      <div class="links">
        <div class="nav-category">
          <div class="nav-category">
            {#if nav}
              <BlockButton
                variant="outline"
                href={nav.navLink.link.url}
                on:click={handleCloseClicked}
                {...topLevelButtonStyles}
              >
                {nav.navLink.link.text}
              </BlockButton>
            {/if}
          </div>
          {#if nav}
            <TwistStack
              direction="column"
              spacing={[
                ['min', 24],
                ['sm', 'lg', 24, 48]
              ]}
              align="center"
            >
              {#each nav.items as item, i}
                <BlockButton
                  variant="outline"
                  href={newsTagPagePath(nav.items[i].linkReference.slug.current)}
                  on:click={handleCloseClicked}
                  data-sanity={encodeDataAttribute([...path, 'items', i, 'text'])}
                >
                  {item.text}
                </BlockButton>
              {/each}
            </TwistStack>
          {/if}
        </div>
        <div class="nav-category">
          <BlockButton
            href="/careers/jobs"
            on:click={handleCloseClicked}
            variant="outline"
            {...topLevelButtonStyles}
          >
            Careers
          </BlockButton>
        </div>
        <div class="nav-category investors-category">
          <BlockButton
            variant="outline"
            href="https://investors.block.xyz/overview/default.aspx"
            target="_blank"
            {...topLevelButtonStyles}
          >
            Investors
          </BlockButton>
        </div>
      </div>
    </div>

    <div class="accessibility-controls">
      <TwistDivider thickness="1px" margin={5} />
      <AccessibilityControls />
    </div>
  </div>
</Modal>

<style lang="scss" descendants>
  @import 'src/scss/accessibility';

  .content {
    padding: 0px var(--twist-core-spacing-4);
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    height: 100%;
    overflow: auto;

    background: radial-gradient(
        closest-side at top right,
        var(--twist-color-gold-30) 20%,
        transparent 30%
      ),
      radial-gradient(farthest-side at bottom right, var(--twist-color-red-0) 30%, transparent 70%),
      radial-gradient(farthest-side at 0% 90%, var(--twist-color-teal-30) 50%, transparent 90%),
      var(--twist-color-gold-30);
  }

  @include prefers-reduce-color {
    .content {
      background: var(--twist-color-gray-10);
    }
  }

  .close {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    color: #fff;
    background: #000;
    border: none;
    border-radius: 1000px;
    cursor: pointer;
    transition:
      background 0.2s ease,
      color 0.2s ease;
    outline: none;

    width: var(--close-size);
    height: var(--close-size);
    top: var(--close-spacer);
    right: var(--close-spacer);

    &:hover,
    &:focus-visible {
      background: #fff;
      color: #000;
    }

    .icon {
      width: var(--close-icon-size);
      height: var(--close-icon-size);
    }
  }

  .accessibility-controls {
    font-size: 12px;
    position: absolute;
    bottom: var(--twist-core-spacing-5);
    width: calc(100% - 2 * var(--twist-core-spacing-4));
  }

  .links-container {
    margin: 110px 0;
    display: flex;
    justify-content: center;
    overflow: auto;
  }

  .nav-category {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-bottom: var(--twist-core-spacing-5);
  }
</style>
